import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PrizeTierBuilder from '../components/PrizeTierBuilder';
import ViralStrategySelector from '../components/ViralStrategySelector';
import GHLConnectionStatus from '../components/GHLConnectionStatus';

function CreateRaffle() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    entry_price: '', // Base price (will be deprecated in favor of packages)
    currency: 'USD',
    max_entries_per_person: '',
    start_date: '',
    end_date: '',
    timezone: 'UTC',
    ticket_prefix: 'RAFFLE-',
    page_template_id: '' // GHL template selection
  });
  const [packages, setPackages] = useState([
    { package_name: 'Single Ticket', ticket_quantity: 1, price: '', description: '', display_order: 1 }
  ]);

  // Multi-winner configuration
  const [usePrizeTiers, setUsePrizeTiers] = useState(false);
  const [totalWinners, setTotalWinners] = useState(1);
  const [prizeTiers, setPrizeTiers] = useState([
    { tierName: 'Winner', tierRankStart: 1, tierRankEnd: 1, prizeName: '', prizeValue: '', displayOrder: 1 }
  ]);

  // Viral strategy configuration
  const [viralStrategy, setViralStrategy] = useState(null);

  // Winner selection configuration
  const [winnerSelectionMode, setWinnerSelectionMode] = useState('manual'); // 'manual' or 'auto'
  const [autoSelectAt, setAutoSelectAt] = useState('');

  // Cash sales configuration
  const [allowCashSales, setAllowCashSales] = useState(false);
  const [requireEmailForCash, setRequireEmailForCash] = useState(true);
  const [cashSalesLocation, setCashSalesLocation] = useState('');

  const [heroImage, setHeroImage] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState('');
  const [uploadingHero, setUploadingHero] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeHeroImage = () => {
    setHeroImage(null);
    setHeroImagePreview('');
  };

  const handlePackageChange = (index, field, value) => {
    const updatedPackages = [...packages];
    updatedPackages[index][field] = value;
    setPackages(updatedPackages);
  };

  const addPackage = () => {
    setPackages([
      ...packages,
      {
        package_name: '',
        ticket_quantity: 1,
        price: '',
        description: '',
        display_order: packages.length + 1
      }
    ]);
  };

  const removePackage = (index) => {
    if (packages.length > 1) {
      const updatedPackages = packages.filter((_, i) => i !== index);
      // Reorder remaining packages
      updatedPackages.forEach((pkg, i) => {
        pkg.display_order = i + 1;
      });
      setPackages(updatedPackages);
    }
  };

  const calculateSavings = (pkg) => {
    if (!pkg.price || !pkg.ticket_quantity || pkg.ticket_quantity === 1) return null;
    const pricePerTicket = parseFloat(pkg.price) / parseInt(pkg.ticket_quantity);
    const basePrice = packages[0]?.price ? parseFloat(packages[0].price) : 0;
    if (basePrice === 0) return null;
    const savings = ((basePrice - pricePerTicket) / basePrice * 100).toFixed(1);
    return savings > 0 ? savings : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate packages
    if (packages.length === 0) {
      setError('Please add at least one ticket package');
      setLoading(false);
      return;
    }

    const invalidPackages = packages.filter(pkg =>
      !pkg.package_name ||
      pkg.price === '' ||
      pkg.price === null ||
      pkg.price === undefined ||
      !pkg.ticket_quantity ||
      parseFloat(pkg.price) < 0 ||
      pkg.ticket_quantity <= 0
    );

    if (invalidPackages.length > 0) {
      setError('Please complete all package fields with valid values');
      setLoading(false);
      return;
    }

    // Validate prize tiers if enabled
    if (usePrizeTiers) {
      const invalidTiers = prizeTiers.filter(tier =>
        !tier.tierName || !tier.prizeName || !tier.tierRankStart || !tier.tierRankEnd
      );

      if (invalidTiers.length > 0) {
        setError('Please complete all prize tier fields');
        setLoading(false);
        return;
      }
    }

    // Validate dates
    if (!formData.start_date || !formData.end_date) {
      setError('Please select both start and end dates');
      setLoading(false);
      return;
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const now = new Date();

    if (endDate <= startDate) {
      setError('End date must be after start date');
      setLoading(false);
      return;
    }

    if (startDate < now) {
      setError('Start date cannot be in the past');
      setLoading(false);
      return;
    }

    // Validate auto-select date if enabled
    if (winnerSelectionMode === 'auto' && autoSelectAt) {
      const autoSelectDate = new Date(autoSelectAt);
      if (autoSelectDate <= endDate) {
        setError('Winner selection date must be after raffle end date');
        setLoading(false);
        return;
      }
    }

    try {
      // Convert datetime-local values to ISO strings (with timezone)
      const startDateISO = new Date(formData.start_date).toISOString();
      const endDateISO = new Date(formData.end_date).toISOString();
      const autoSelectAtISO = winnerSelectionMode === 'auto' && autoSelectAt
        ? new Date(autoSelectAt).toISOString()
        : null;

      console.log('📅 Date conversion:', {
        startLocal: formData.start_date,
        startUTC: startDateISO,
        endLocal: formData.end_date,
        endUTC: endDateISO,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      // Step 1: Create the raffle
      const raffleResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/raffles`,
        {
          ...formData,
          start_date: startDateISO,
          end_date: endDateISO,
          packages: packages,
          use_prize_tiers: usePrizeTiers,
          total_winners: totalWinners,
          // Winner selection
          winner_selection_mode: winnerSelectionMode,
          auto_select_winners: winnerSelectionMode === 'auto',
          auto_select_at: autoSelectAtISO,
          // Cash sales
          allow_cash_sales: allowCashSales,
          require_email_for_cash: requireEmailForCash,
          cash_sales_location: allowCashSales ? cashSalesLocation : null
        }
      );

      // Backend returns { success: true, raffle: {...}, setupInstructions: {...} }
      const raffleId = raffleResponse.data.raffle?.id || raffleResponse.data.data?.id;

      console.log('✅ Raffle created successfully:', {
        raffleId,
        hasInstructions: !!raffleResponse.data.setupInstructions,
        response: raffleResponse.data
      });

      // Step 2: Create prize tiers if enabled
      if (usePrizeTiers && prizeTiers.length > 0) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/prize-tiers/${raffleId}/bulk`,
          { tiers: prizeTiers }
        );
      }

      // Step 3: Save viral strategy if selected
      if (viralStrategy) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/viral-strategies/${raffleId}`,
          viralStrategy
        );
      }

      // Step 4: Upload hero image if provided
      if (heroImage) {
        setUploadingHero(true);

        const formData = new FormData();
        formData.append('heroImage', heroImage);

        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/raffles/${raffleId}/hero-image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          console.log('✅ Hero image uploaded to GHL Media Library');
        } catch (heroError) {
          console.error('Failed to upload hero image:', heroError);
          // Don't fail the entire raffle creation if hero upload fails
        } finally {
          setUploadingHero(false);
        }
      }

      // Navigate to raffle details
      navigate(`/raffles/${raffleId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create raffle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <GHLConnectionStatus />

      <div className="card" style={{ maxWidth: '600px', margin: '50px auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '15px'
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ margin: 0 }}>Create New Raffle</h1>
        </div>
        <form onSubmit={handleSubmit}>
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />

          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
          />

          {/* Hero Image Upload */}
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>Hero Image (Optional)</label>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              Upload a banner image for your raffle page (recommended: 1200x400px)
            </p>

            {heroImagePreview ? (
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <img
                  src={heroImagePreview}
                  alt="Hero preview"
                  style={{
                    width: '100%',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6'
                  }}
                />
                <button
                  type="button"
                  onClick={removeHeroImage}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={handleHeroImageChange}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px',
                  border: '2px dashed #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            )}
          </div>

          <label>Currency</label>
          <select name="currency" value={formData.currency} onChange={handleChange}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>

          {/* Ticket Packages Section */}
          <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Ticket Packages</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              Create custom ticket packages with bulk discounts. Each package will be a separate product in GHL.
            </p>

            {packages.map((pkg, index) => {
              const savings = calculateSavings(pkg);
              const pricePerTicket = pkg.price && pkg.ticket_quantity ?
                (parseFloat(pkg.price) / parseInt(pkg.ticket_quantity)).toFixed(2) : '0.00';

              return (
                <div key={index} style={{
                  background: 'white',
                  padding: '15px',
                  marginBottom: '15px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <strong style={{ color: '#495057' }}>Package #{index + 1}</strong>
                    {packages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePackage(index)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '5px 10px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <label style={{ marginTop: '10px' }}>Package Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., 'Single Ticket', 'Best Value Bundle', 'VIP Package'"
                    value={pkg.package_name}
                    onChange={(e) => handlePackageChange(index, 'package_name', e.target.value)}
                    required
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label>Number of Tickets *</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="1"
                        value={pkg.ticket_quantity}
                        onChange={(e) => handlePackageChange(index, 'ticket_quantity', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label>Package Price ({formData.currency}) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={pkg.price}
                        onChange={(e) => handlePackageChange(index, 'price', e.target.value)}
                        required
                      />
                      <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                        Set to 0 for free raffles
                      </small>
                    </div>
                  </div>

                  <label>Description (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., 'Best deal - Save 30%!'"
                    value={pkg.description}
                    onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                  />

                  {/* Price Analysis */}
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    background: '#e7f3ff',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>Price per ticket:</span>
                      <strong>{formData.currency} {pricePerTicket}</strong>
                    </div>
                    {savings && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#28a745' }}>
                        <span>💰 Savings vs Single:</span>
                        <strong>{savings}% OFF</strong>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={addPackage}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 20px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              + Add Another Package
            </button>
          </div>

          <label>Max Entries Per Person (optional)</label>
          <input
            type="number"
            name="max_entries_per_person"
            value={formData.max_entries_per_person}
            onChange={handleChange}
          />

          <label>Start Date</label>
          <input
            type="datetime-local"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            required
          />

          <label>End Date</label>
          <input
            type="datetime-local"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            min={formData.start_date}
            required
          />

          <label>Ticket Prefix</label>
          <input
            type="text"
            name="ticket_prefix"
            value={formData.ticket_prefix}
            onChange={handleChange}
          />

          {/* Multi-Winner Configuration Section */}
          <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="usePrizeTiers"
                checked={usePrizeTiers}
                onChange={(e) => setUsePrizeTiers(e.target.checked)}
                style={{ marginRight: '10px', width: 'auto' }}
              />
              <label htmlFor="usePrizeTiers" style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                🏆 Enable Multi-Winner with Prize Tiers
              </label>
            </div>
            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 15px 28px' }}>
              Select multiple winners with different prizes (1st place, 2nd place, etc.) instead of a single winner.
            </p>

            {usePrizeTiers && (
              <PrizeTierBuilder
                tiers={prizeTiers}
                onChange={setPrizeTiers}
                totalWinners={totalWinners}
                onTotalWinnersChange={setTotalWinners}
              />
            )}
          </div>

          {/* Viral Strategy Section */}
          <div style={{ marginTop: '30px' }}>
            <ViralStrategySelector
              selectedStrategy={viralStrategy}
              onChange={setViralStrategy}
            />
          </div>

          {/* Winner Selection Method */}
          <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '10px' }}>🎯 Winner Selection Method</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              Choose how winners will be selected after the raffle ends
            </p>

            <div style={{ marginLeft: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <input
                  type="radio"
                  id="manualSelection"
                  name="winnerSelectionMode"
                  value="manual"
                  checked={winnerSelectionMode === 'manual'}
                  onChange={(e) => setWinnerSelectionMode(e.target.value)}
                  style={{ marginRight: '10px', width: 'auto' }}
                />
                <label htmlFor="manualSelection" style={{ margin: 0, cursor: 'pointer' }}>
                  <strong>Manual Selection</strong> - I will click "Select Winners" button when ready
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="radio"
                  id="autoSelection"
                  name="winnerSelectionMode"
                  value="auto"
                  checked={winnerSelectionMode === 'auto'}
                  onChange={(e) => setWinnerSelectionMode(e.target.value)}
                  style={{ marginRight: '10px', width: 'auto' }}
                />
                <label htmlFor="autoSelection" style={{ margin: 0, cursor: 'pointer' }}>
                  <strong>Automatic Selection</strong> - Winners selected automatically at scheduled time
                </label>
              </div>

              {winnerSelectionMode === 'auto' && (
                <div style={{ marginLeft: '28px', marginTop: '15px' }}>
                  <label style={{ fontSize: '13px', color: '#666' }}>Winner Selection Date & Time</label>
                  <input
                    type="datetime-local"
                    value={autoSelectAt}
                    onChange={(e) => setAutoSelectAt(e.target.value)}
                    required={winnerSelectionMode === 'auto'}
                    style={{ marginTop: '5px' }}
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: 0 }}>
                    Winners will be automatically selected at this time. Raffle must end first.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Cash Sales Section */}
          <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="allowCashSales"
                checked={allowCashSales}
                onChange={(e) => setAllowCashSales(e.target.checked)}
                style={{ marginRight: '10px', width: 'auto' }}
              />
              <label htmlFor="allowCashSales" style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                💵 Enable In-Person Cash Ticket Sales
              </label>
            </div>
            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 15px 28px' }}>
              Allow staff to manually sell tickets for cash at local events
            </p>

            {allowCashSales && (
              <div style={{ marginLeft: '28px' }}>
                <label>Cash Sales Location</label>
                <input
                  type="text"
                  value={cashSalesLocation}
                  onChange={(e) => setCashSalesLocation(e.target.value)}
                  placeholder="e.g., Main Event Booth, Community Center"
                  required={allowCashSales}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Default location where cash sales occur
                </p>

                <div style={{ display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                  <input
                    type="checkbox"
                    id="requireEmailForCash"
                    checked={requireEmailForCash}
                    onChange={(e) => setRequireEmailForCash(e.target.checked)}
                    style={{ marginRight: '10px', width: 'auto' }}
                  />
                  <label htmlFor="requireEmailForCash" style={{ margin: 0, cursor: 'pointer', fontSize: '14px' }}>
                    Require email address for cash entries
                  </label>
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: 0, marginLeft: '28px' }}>
                  If unchecked, staff can create entries with just customer name and phone
                </p>
              </div>
            )}
          </div>

          {error && <div className="error">{error}</div>}

          <div style={{ marginTop: '20px' }}>
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Raffle'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              style={{ marginLeft: '10px', background: '#6c757d' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateRaffle;
